<template>
  <div class="flex justify-center mt-6 w-full input-component relative">
    <PhonePrefix
      class="w-full"
      v-model="prefix"
      @selectedPrefix="handleSelectedPrefix"
    />

    <div class="flex w-full input-component relative">
      <SvgContainer name="inputPhoneIcon" />

      <input
        class="input-rounded"
        :class="{ 'is-invalid': !isValid }"
        :readonly="readonly"
        type="text"
        inputmode="tel"
        id="phone"
        maxlength="40"
        :placeholder="$t('phone_number')"
        v-model="phone"
        @input="handleInput"
        @blur="handleBlur"
      />
    </div>
  </div>

  <div class="flex justify-start">
    <p v-if="error" class="error-message max-w-xs whitespace-normal">
      {{ error }}
    </p>
  </div>
</template>

<script>
import SvgContainer from "@/views/SvgContainer.vue";
import PhonePrefix from "@/components/countryFlagSelector/phonePrefix.vue";
import { Format } from "@/helpers/formatHelper";

export default {
  name: "inputPhone",
  components: { SvgContainer, PhonePrefix },
  emits: ["phoneValidated"],

  props: {
    readonly: {
      type: Boolean,
      default: false,
    },
    initialPhone: {
      type: String,
      default: "",
    },
    initialPrefix: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      phone: "",
      prefix: "",
      error: "",
    };
  },

  computed: {
    isValid() {
      return this.error === "";
    },

    prefixDigits() {
      return Format.sanitizePhone(String(this.prefix ?? "")) || "";
    },
  },

  watch: {
    initialPhone(newVal) {
      this.phone = newVal || "";
      this.renormalize();
    },
    initialPrefix(newVal) {
      this.prefix = newVal;
      this.renormalize();
    },
  },

  mounted() {
    if (this.initialPrefix) this.prefix = this.initialPrefix;
    if (this.initialPhone) this.phone = this.initialPhone;

    if (this.initialPhone || this.initialPrefix) {
      this.renormalize();
    }
  },

  methods: {
    toLocalNumber(value) {
      return Format.toLocalPhone(value, this.prefix);
    },

    handleInput(event) {
      const input = event.target;
      const typed = input.value;
      const sanitized = Format.sanitizeTypedPhone(typed);

      if (typed !== sanitized) {
        const caret = input.selectionStart ?? typed.length;
        const removedBeforeCaret =
          caret - Format.sanitizeTypedPhone(typed.slice(0, caret)).length;
        input.value = sanitized;
        const position = caret - removedBeforeCaret;
        input.setSelectionRange(position, position);
      }

      this.phone = sanitized;
      this.validatePhone();
    },

    renormalize() {
      this.phone = this.toLocalNumber(this.phone || "");
      this.validatePhone();
    },

    handleBlur() {
      this.renormalize();
    },

    validatePhone() {
      const number = this.toLocalNumber(this.phone || "");
      const digits = Format.sanitizePhone(number);

      if (!this.phone) {
        this.error = this.$t("the_phone_number_cannot_be_empty");
        this.$emit("phoneValidated", null);
      } else if (digits.length < 6) {
        this.error = this.$t("phone_min");
        this.$emit("phoneValidated", null);
      } else if (number.startsWith("+")) {
        this.error = this.$t("phone_prefix_mismatch");
        this.$emit("phoneValidated", null);
      } else if (
        digits.length + this.prefixDigits.length >
        Format.MAX_PHONE_DIGITS
      ) {
        this.error = this.$t("phone_max");
        this.$emit("phoneValidated", null);
      } else {
        this.error = "";
        this.$emit("phoneValidated", number, this.prefix);
      }
    },

    clear() {
      this.phone = "";
    },

    handleSelectedPrefix(prefix) {
      this.prefix = prefix;

      if (!this.phone) {
        this.$emit("phoneValidated", this.phone, this.prefix);
        return;
      }

      this.renormalize();
    },
  },
};
</script>
